import React from "react";
import { Link } from "react-router-dom";

type Service = {
  icon: string;
  title: string;
  description: string;
  features: string[];
};

const ServiceCard: React.FC<Service> = ({
  icon,
  title,
  description,
  features,
}) => (
  <Link
    className="service-card p-lg bg-surface-container-low border border-outline-variant rounded-xl group block h-full cursor-pointer transition-all duration-300 hover:bg-surface-container-high"
    to="/services"
    aria-label={`Open services page for ${title}`}
  >
    <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-md group-hover:bg-secondary transition-colors">
      <span
        className="material-symbols-outlined text-secondary group-hover:text-white"
        style={{ fontVariationSettings: "'FILL' 1" }}
      >
        {icon}
      </span>
    </div>
    <h3 className="font-headline-md text-headline-md text-primary mb-sm">
      {title}
    </h3>
    <p className="font-body-md text-on-surface-variant mb-md">{description}</p>
    <ul className="space-y-sm">
      {features.map((feature) => (
        <li
          key={feature}
          className="flex items-center gap-sm text-on-surface-variant"
        >
          <span className="material-symbols-outlined text-secondary text-sm">
            check_circle
          </span>
          {feature}
        </li>
      ))}
    </ul>
  </Link>
);

export const ServicesSection: React.FC = () => {
  const services: Service[] = [
    {
      icon: "home_health",
      title: "Residential",
      description:
        "Meticulous home cleaning that transforms your living space into a hygienic sanctuary.",
      features: [
        "Deep Sanitization",
        "Eco-friendly products",
        "Flexible scheduling",
      ],
    },
    {
      icon: "corporate_fare",
      title: "Commercial",
      description:
        "High-impact maintenance for offices and facilities requiring institutional-grade cleanliness.",
      features: [
        "Janitorial Services",
        "Surface Disinfection",
        "Post-Construction",
      ],
    },
    {
      icon: "pest_control",
      title: "Pest Control",
      description:
        "Effective, safe, and professional pest management solutions for peace of mind.",
      features: [
        "Prevention focus",
        "Targeted treatments",
        "Professional audits",
      ],
    },
  ];

  return (
    <section className="py-xl bg-surface">
      <div className="max-w-container-max mx-auto px-md">
        <div className="text-center mb-lg">
          <h2 className="font-headline-lg text-headline-lg text-primary mb-sm">
            Our Services
          </h2>
          <p className="font-body-md text-on-surface-variant max-w-2xl mx-auto">
            Providing specialized cleaning solutions tailored to your unique
            environment.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-md">
          {services.map((service) => (
            <ServiceCard key={service.title} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
};
